import { useMemo } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAuthStore } from '@/store/authStore';
import { useAnnouncementDismiss } from './useAnnouncementDismiss';
import { useAnnouncementTracking } from './useAnnouncementTracking';
import { useAnnouncementDelay } from './useAnnouncementDelay';
import type { AnnouncementConfig, AnnouncementState, AnnouncementDisplayType } from '../types';

/**
 * Main hook for managing announcement state.
 * Combines feature flag check, dismissal state, role targeting, and analytics tracking.
 */
export function useAnnouncement(
  config: AnnouncementConfig,
  displayType?: AnnouncementDisplayType
): AnnouncementState {
  const { roles } = useAuthStore();
  const flagEnabled = useFeatureFlagEnabled(config.featureFlag);
  const { isDismissed, dismiss, isLoading } = useAnnouncementDismiss(config.id, displayType);
  const { trackImpression, trackCtaClick, trackDismiss } = useAnnouncementTracking(config.id);

  // Check if announcements should be suppressed due to onboarding
  const { shouldSuppressAnnouncements, wentThroughOnboarding, isLoading: isLoadingOnboarding } =
    useAnnouncementDelay();

  // Check if user has a matching role (if targeting is specified)
  const hasMatchingRole = useMemo(() => {
    if (!config.targetRoles || config.targetRoles.length === 0) {
      return true; // No role targeting, show to everyone
    }
    return config.targetRoles.some((role) => roles.includes(role));
  }, [config.targetRoles, roles]);

  // Check if within date range
  const isWithinDateRange = useMemo(() => {
    const now = new Date();
    if (config.startDate && new Date(config.startDate) > now) {
      return false;
    }
    if (config.endDate && new Date(config.endDate) < now) {
      return false;
    }
    return true;
  }, [config.startDate, config.endDate]);

  // Permanently hide existingUsersOnly announcements for users who went through onboarding
  const isExcludedForNewUser = config.existingUsersOnly === true && wentThroughOnboarding;

  const isVisible = Boolean(
    flagEnabled && !isDismissed && !isLoading && !isLoadingOnboarding && !shouldSuppressAnnouncements && !isExcludedForNewUser && hasMatchingRole && isWithinDateRange
  );

  const handleDismiss = async (displayType?: AnnouncementDisplayType) => {
    if (displayType) {
      trackDismiss(displayType);
    }
    await dismiss();
  };

  return {
    isVisible,
    isDismissed,
    isLoading,
    dismiss: () => handleDismiss(),
    trackImpression,
    trackCtaClick,
  };
}
